/**
 * @fileoverview Mock-only fake "other devices" session list.
 * @description The mock has no real session table, so it fabricates one other live session per
 * user (to give the Settings page's sessions panel something to revoke) plus a synthesized
 * "current" entry derived from whichever request is asking. In-memory only (not persisted to
 * localStorage) — losing the fabricated list on a page reload is an acceptable mock-only
 * limitation. Never imported outside this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import type { SessionSummary } from '@/features/auth/services/session.service'

const otherSessionsByUser = new Map<string, SessionSummary[]>()

/**
 * @description Returns a user's fake "other device" sessions, seeding one on first access.
 * @param {string} userId - The mock user id.
 * @returns {SessionSummary[]} That user's other live sessions.
 */
export function getOtherSessions(userId: string): SessionSummary[] {
  const existing = otherSessionsByUser.get(userId)

  if (existing) {
    return existing
  }

  const seeded: SessionSummary[] = [
    {
      sessionId: `mock-session.${userId}.other-device`,
      deviceId: `mock-device.${userId}.other-device`,
      deviceName: 'Safari on iPhone',
      startedAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      lastActivityAt: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(),
      absoluteExpiresAt: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString(),
      idleExpiresAt: null,
      isCurrent: false,
    },
  ]

  otherSessionsByUser.set(userId, seeded)
  return seeded
}

/**
 * @description Builds the synthesized "current" session entry for whichever request is asking.
 * @param {string} userId - The mock user id.
 * @returns {SessionSummary} The current session entry.
 */
export function getCurrentSession(userId: string): SessionSummary {
  return {
    sessionId: `mock-session.${userId}.current`,
    deviceId: `mock-device.${userId}.current`,
    deviceName: 'Chrome on Windows',
    startedAt: new Date().toISOString(),
    lastActivityAt: new Date().toISOString(),
    absoluteExpiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    idleExpiresAt: null,
    isCurrent: true,
  }
}

/**
 * @description Removes one of a user's fake "other device" sessions.
 * @param {string} userId - The mock user id.
 * @param {string} sessionId - The session to remove.
 */
export function revokeOtherSession(userId: string, sessionId: string): void {
  otherSessionsByUser.set(
    userId,
    getOtherSessions(userId).filter((session) => session.sessionId !== sessionId),
  )
}

/**
 * @description Clears every fake "other device" session for a user, e.g. on logout-all.
 * @param {string} userId - The mock user id.
 * @returns {number} How many other sessions were cleared.
 */
export function clearOtherSessions(userId: string): number {
  const count = getOtherSessions(userId).length
  otherSessionsByUser.set(userId, [])
  return count
}
